import React, { useEffect, useRef, useState } from "react";
import { getAppParams } from "./utils/appParams";
import "./App.css";
import useAuth from "./hooks/auth";
import Loader from "./components/loader";
import { licenseSN, licenseKey } from "./license-key";
import axios from "axios";
import { Modal, Input, message } from "antd";
import DebugErrorScreen from "./components/DebugErrorScreen";
import { buildServiceNowUrl, getServiceNowHeaders } from "./services/serviceNow";

export default function PdfEditor() {
  const {
    fileBuffer,
    fileBlob,
    fetchPdfFromSharePoint,
    loading,
    authError,
    clearAuthError,
  } = useAuth();

  const containerRef = useRef(null);
  const pdfuiRef = useRef(null);
  const [foxitReady, setFoxitReady] = useState(Boolean(window.UIExtension && window.UIExtension.PDFUI));
  const [viewerInitError, setViewerInitError] = useState(null);

  // Inject Foxit assets like the smart_folders test page
  useEffect(() => {
    if (foxitReady) return;
    const envWin = (typeof window !== 'undefined' && window.env) ? window.env : {};
    const configuredBase = envWin.REACT_APP_FOXIT_LIB_BASE;
    const LOCAL_BASE = configuredBase || '/foxit-lib/';
    const setFoxitLoadError = (reason) => {
      setViewerInitError({
        code: 'FOXIT_INIT_FAILED',
        message:
          'PDF viewer failed to initialize. This environment is likely blocking Foxit runtime scripts via CSP.',
        details: {
          reason,
          foxitLibBase: LOCAL_BASE,
          hint:
            "Allow Foxit runtime execution (including 'unsafe-eval') in App Service CSP, or use a CSP-compatible Foxit build.",
        },
      });
    };
    function injectCss(href) { const l = document.createElement('link'); l.rel = 'stylesheet'; l.href = href; document.head.appendChild(l); return l; }
    function injectScript(src, onload, onerror) { const s = document.createElement('script'); s.src = src; s.async = true; s.onload = onload; if (onerror) s.onerror = onerror; document.head.appendChild(s); return s; }
    injectCss(LOCAL_BASE + 'UIExtension.css');
    window.__foxitLibBase = LOCAL_BASE;
    injectScript(
      LOCAL_BASE + 'UIExtension.full.js',
      () => {
        if (window.UIExtension && window.UIExtension.PDFUI) {
          setFoxitReady(true);
          return;
        }
        setFoxitLoadError('UIExtension script loaded but window.UIExtension.PDFUI is unavailable');
      },
      () => {
        setFoxitLoadError('Failed to load UIExtension.full.js');
      }
    );
  }, [foxitReady]);

  // Fetch file if needed
  useEffect(() => {
    if (!fileBuffer && !fileBlob) { fetchPdfFromSharePoint(); }
  }, []);

  // Create Foxit viewer when ready
  useEffect(() => {
    if (!foxitReady) return;
    if (!window.UIExtension || !window.UIExtension.PDFUI) {
      setViewerInitError({
        code: 'FOXIT_RUNTIME_MISSING',
        message: 'Foxit runtime is unavailable after script load.',
        details: {
          hint:
            "Check App Service Content-Security-Policy. Current runtime path requires script execution that may be blocked.",
        },
      });
      return;
    }
    if (!containerRef.current) return;
    if (pdfuiRef.current) return;
    const libPath = window.__foxitLibBase || '/foxit-lib/';
    try {
      const installPageMoveLogging = async () => {
        try {
          const pdfui = pdfuiRef.current;
          if (!pdfui) return;
          const pdfDoc = await pdfui.getCurrentPDFDoc();
          if (!pdfDoc || pdfDoc.__moveLoggingInstalled) return;
          const wrap = (obj, methodName) => {
            try {
              const original = obj && obj[methodName];
              if (typeof original !== 'function') return;
              obj[methodName] = function (...args) {
                try {
                  // eslint-disable-next-line no-console
                  console.log('[Foxit] Page move attempt (before)', { method: methodName, args });
                } catch (_) {}
                return original.apply(this, args);
              };
            } catch (_) {}
          };
          // Common methods used by Organize/Thumbnails when reordering pages
          wrap(pdfDoc, 'movePages');
          wrap(pdfDoc, 'movePage');

          // Also attempt prototype-level wrapping to catch new instances or internal calls
          try {
            const proto = Object.getPrototypeOf(pdfDoc) || pdfDoc.__proto__;
            const names = Object.getOwnPropertyNames(proto || {}).filter((n) => /move.*Page|reorder|rearrange/i.test(String(n)));
            for (const name of names) { wrap(proto, name); }
          } catch (_) {}

          // Intercept high-level UI commands that might trigger page moves
          try {
            if (!pdfui.__execWrapped && typeof pdfui.exec === 'function') {
              const originalExec = pdfui.exec.bind(pdfui);
              pdfui.exec = function (...args) {
                try {
                  const cmd = args && args[0];
                  if (cmd && /move|reorder|organize|arrang/i.test(String(cmd))) {
                    // eslint-disable-next-line no-console
                    console.log('[Foxit] pdfui.exec (before)', cmd, args.slice(1));
                  }
                } catch (_) {}
                return originalExec(...args);
              };
              pdfui.__execWrapped = true;
            }
          } catch (_) {}

          pdfDoc.__moveLoggingInstalled = true;
        } catch (_) {}
      };

      const setupThumbnailMoveLogging = () => {
        try {
          if (window.__sfThumbDnDLoggingInstalled) return;
          window.__sfThumbDnDLoggingInstalled = true;
          const getPageIndex = (el) => {
            try {
              if (!el) return undefined;
              const withAttr = el.closest && (el.closest('[data-page-index]') || el.closest('[data-index]'));
              if (withAttr) {
                const v = withAttr.getAttribute('data-page-index') || withAttr.getAttribute('data-index');
                const n = parseInt(v, 10);
                return Number.isFinite(n) ? n : undefined;
              }
            } catch (_) {}
            return undefined;
          };
          const isInsideThumbs = (el) => {
            try {
              if (!el || !el.closest) return false;
              return Boolean(
                el.closest('.fv__thumbnails-panel') ||
                el.closest('.fv__panel--thumbnails') ||
                el.closest('[data-name="thumbnails-panel"]') ||
                el.closest('[data-module="thumbnails"]')
              );
            } catch (_) { return false; }
          };
          let dragFromIndex;
          document.addEventListener('dragstart', (e) => {
            try {
              const t = e.target;
              if (!isInsideThumbs(t)) return;
              dragFromIndex = getPageIndex(t);
              // eslint-disable-next-line no-console
              console.log('[Foxit] Thumbnail drag start', { fromIndex: dragFromIndex });
            } catch (_) {}
          }, true);
          document.addEventListener('drop', (e) => {
            try {
              const t = e.target;
              if (!isInsideThumbs(t)) return;
              const toIndex = getPageIndex(t);
              // eslint-disable-next-line no-console
              console.log('[Foxit] Thumbnail drop (attempt move)', { fromIndex: dragFromIndex, toIndex });
            } catch (_) {}
          }, true);
          document.addEventListener('dragend', () => { dragFromIndex = undefined; }, true);

          // Observe DOM to catch panel appearing later (no-op if already installed)
          try {
            const observer = new MutationObserver(() => {});
            observer.observe(document.body, { childList: true, subtree: true });
          } catch (_) {}
        } catch (_) {}
      };
      // Ensure full UIX addons (Organize, Thumbnails, Edit, etc.) are loaded before creating PDFUI
      const ensureAddonsLoaded = async () => {
        if (window.UIXAddons && Array.isArray(window.UIXAddons) && window.UIXAddons.length > 0) return;
        const isMobile = !!(window.UIExtension && window.UIExtension.PDFViewCtrl && window.UIExtension.PDFViewCtrl.DeviceInfo && window.UIExtension.PDFViewCtrl.DeviceInfo.isMobile);
        const addonSrc = libPath + 'uix-addons/' + (isMobile ? 'allInOne.mobile.js' : 'allInOne.js');
        await new Promise((resolve, reject) => {
          const s = document.createElement('script');
          s.src = addonSrc;
          s.async = true;
          s.onload = () => resolve();
          s.onerror = (e) => reject(e);
          document.head.appendChild(s);
        });
      };

      const fragments = [
        // Insert in reverse so final visual order after the Download button is: Save, Save As, Extract, Burst
        {
          target: 'download-file-button',
          action: 'after',
          template: '<div class="sf-toolbar-combo" data-sf="burst"><xbutton name="sf-burst-pdf" class="sf-toolbar-icon-40"></xbutton><span class="sf-toolbar-text">Burst</span></div>'
        },
        {
          target: 'download-file-button',
          action: 'after',
          template: '<div class="sf-toolbar-combo" data-sf="extract"><xbutton name="sf-extract-pages" class="sf-toolbar-icon-40"></xbutton><span class="sf-toolbar-text">Extract</span></div>'
        },
        {
          target: 'download-file-button',
          action: 'after',
          template: '<div class="sf-toolbar-combo" data-sf="saveas"><xbutton name="sf-saveas-pdf" class="sf-toolbar-icon-40"></xbutton><span class="sf-toolbar-text">Save As</span></div>'
        },
        {
          target: 'download-file-button',
          action: 'after',
          template: '<div class="sf-toolbar-combo" data-sf="save"><xbutton name="sf-save-pdf" class="sf-toolbar-icon-40"></xbutton><span class="sf-toolbar-text">Save</span></div>'
        },
        {
          target: 'sf-extract-pages',
          config: {
            tooltip: { title: 'Extract specific pages into a new PDF' },
            text: 'Extract',
            showText: true,
            iconCls: 'fv__icon-extract sf-toolbar-icon-24',
            callback: function () { try { if (window.__sfExtract) window.__sfExtract(); } catch (e) { /* eslint-disable-next-line no-console */ console.error(e); } }
          }
        },
        {
          target: 'sf-save-pdf',
          config: {
            tooltip: { title: 'Save current document' },
            text: 'Save',
            showText: true,
            iconCls: 'fv__icon-save sf-toolbar-icon-24',
            callback: function () { try { if (window.__sfSave) window.__sfSave(); } catch (e) { /* eslint-disable-next-line no-console */ console.error(e); } }
          }
        },
        {
          target: 'sf-saveas-pdf',
          config: {
            tooltip: { title: 'Save As a new document' },
            text: 'Save As',
            showText: true,
            iconCls: 'fv__icon-saveas sf-toolbar-icon-24',
            callback: function () { try { if (window.__sfSaveAs) window.__sfSaveAs(); } catch (e) { /* eslint-disable-next-line no-console */ console.error(e); } }
          }
        },
        {
          target: 'sf-burst-pdf',
          config: {
            tooltip: { title: 'Split the PDF into multiple files' },
            text: 'Burst',
            showText: true,
            iconCls: 'fv__icon-burst sf-toolbar-icon-24',
            callback: function () { try { if (window.__sfBurst) window.__sfBurst(); } catch (e) { /* eslint-disable-next-line no-console */ console.error(e); } }
          }
        }
      ];
      (async () => {
        await ensureAddonsLoaded();
        const pdfui = new window.UIExtension.PDFUI({
          viewerOptions: {
            libPath,
            jr: {
              workerPath: libPath,
              enginePath: libPath + 'jr-engine/gsdk',
              fontPath: 'https://webpdf.foxitsoftware.com/webfonts/',
              licenseSN,
              licenseKey,
              enableServiceWorker: false
            }
          },
          renderTo: containerRef.current,
          appearance: window.UIExtension.appearances.ribbon,
          fragments: fragments,
          addons: (window.UIXAddons || [])
        });
        pdfuiRef.current = pdfui;

        // If we already have the bytes, open immediately
        try {
          let buffer;
          if (fileBuffer && typeof fileBuffer.arrayBuffer === 'function') buffer = await fileBuffer.arrayBuffer();
          else if (fileBlob && typeof fileBlob.arrayBuffer === 'function') buffer = await fileBlob.arrayBuffer();
          if (buffer) {
            await pdfui.openPDFByFile(buffer, { name: 'document.pdf' });
            // Enable logging when users try to move pages via the previewer
            await installPageMoveLogging();
            setupThumbnailMoveLogging();
          }
        } catch (_) {}
      })();
      // Style custom toolbar buttons to match Foxit: bigger icon, centered, text below
      try {
        const styleId = 'sf-toolbar-buttons-style';
        const css = [
          '.sf-toolbar-combo{min-width:40px !important; display:flex !important; flex-direction:column !important; align-items:center !important; justify-content:center !important; padding:4px 8px !important;}',
          '.sf-toolbar-combo .sf-toolbar-text{font-size:12px !important; line-height:1 !important; margin-top:4px !important; text-align:center !important;}',
          '.sf-toolbar-icon-24{width:24px !important;height:24px !important;background-size:24px 24px !important;margin:0 auto !important;display:inline-block !important;}'
        ].join('');
        let style = document.getElementById(styleId);
        if (!style) {
          style = document.createElement('style');
          style.id = styleId;
          document.head.appendChild(style);
        }
        style.textContent = css;
      } catch(_) { }

      // no-op: labels are real DOM siblings via the wrapper
      window.addEventListener('resize', () => { try { pdfuiRef.current && pdfuiRef.current.redraw(); } catch(_) {} });

    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[Foxit] Failed to create PDFUI', e);
    }
  }, [foxitReady]);

  // Open PDF once we have viewer and bytes
  useEffect(() => {
    const pdfui = pdfuiRef.current;
    if (!pdfui) return;
    (async () => {
      try {
        let buffer;
        if (fileBuffer && typeof fileBuffer.arrayBuffer === 'function') buffer = await fileBuffer.arrayBuffer();
        else if (fileBlob && typeof fileBlob.arrayBuffer === 'function') buffer = await fileBlob.arrayBuffer();
        if (!buffer) return;
        await pdfui.openPDFByFile(buffer, { name: 'document.pdf' });
        // Ensure logging is installed even when the file opens later
        try {
          const pdfDoc = await pdfui.getCurrentPDFDoc();
          if (pdfDoc && !pdfDoc.__moveLoggingInstalled) {
            const originalMovePages = typeof pdfDoc.movePages === 'function' ? pdfDoc.movePages : null;
            if (originalMovePages) {
              pdfDoc.movePages = function (...args) {
                try { console.log('[Foxit] Page move attempt (before)', { method: 'movePages', args }); } catch (_) {}
                return originalMovePages.apply(this, args);
              };
            }
            const originalMovePage = typeof pdfDoc.movePage === 'function' ? pdfDoc.movePage : null;
            if (originalMovePage) {
              pdfDoc.movePage = function (...args) {
                try { console.log('[Foxit] Page move attempt (before)', { method: 'movePage', args }); } catch (_) {}
                return originalMovePage.apply(this, args);
              };
            }
            pdfDoc.__moveLoggingInstalled = true;
          }
        } catch (_) {}
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[Foxit] open error', e);
      }
    })();
  }, [fileBuffer, fileBlob, foxitReady]);

  useEffect(() => () => { try { pdfuiRef.current?.destroy?.(); } catch(_) {} }, []);

  // Expose handlers so UIExtension callbacks can call them safely
  useEffect(() => {
    window.__sfExtract = extractPagesAction;
    window.__sfBurst = burstPdfAction;
    window.__sfSave = savePdfAction;
    window.__sfSaveAs = saveAsPdfAction;
    return () => {
      try {
        delete window.__sfExtract;
        delete window.__sfBurst;
        delete window.__sfSave;
        delete window.__sfSaveAs;
      } catch (_) {}
    };
  }, []);

  // Helpers for extract/burst
  const API_ATTACHMENT_UPLOAD = '/api/x_verge_gather/smart_folders/upload_attachment';

  const describeAxiosError = (e) => {
    try {
      const resp = e?.response;
      const apiMsg =
        resp?.data?.error ||
        resp?.data?.message ||
        (typeof resp?.data === 'string' ? resp.data : null);
      if (apiMsg) return String(apiMsg);
      return String(e?.message || e);
    } catch (_) {
      return String(e?.message || e);
    }
  };

  const getUploadContext = () => {
    try {
      const params = getAppParams();
      let table = params.get('table');
      let sys_id = params.get('sys_id');
      let folderId = params.get('folderId');
      let folderName = params.get('folderName');
      let parentNumber = params.get('parentNumber');
      let name = params.get('name');
      // IMPORTANT: Smart Folders provides this so ServiceNow can impersonate the correct end user.
      let user_sys_id =
        params.get('user_sys_id') ||
        params.get('userSysId') ||
        params.get('sn_user_sys_id');

      if (!table || !sys_id || !user_sys_id) {
        const raw = localStorage.getItem('sf_upload_ctx');
        if (raw) {
          const parsed = JSON.parse(raw);
          table = table || parsed.table;
          sys_id = sys_id || parsed.sys_id;
          folderId = folderId || parsed.folderId;
          folderName = folderName || parsed.folderName;
          parentNumber = parentNumber || parsed.parentNumber;
          user_sys_id = user_sys_id || parsed.user_sys_id || parsed.userSysId || parsed.sn_user_sys_id;
        }
      }
      return { table, sys_id, folderId, folderName, parentNumber, name, user_sys_id };
    } catch (_) {
      return {};
    }
  };

  const logRequestDetails = (label, details) => {
    try {
      console.log(`[PdfEditor] ${label}`, details);
    } catch (_) {}
  };

  const arrayBufferToBase64 = async (data) => {
    const blob = data instanceof Blob ? data : new Blob([data], { type: 'application/pdf' });
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result;
        if (typeof result === 'string') {
          const commaIdx = result.indexOf(',');
          resolve(commaIdx >= 0 ? result.slice(commaIdx + 1) : result);
        } else {
          reject(new Error('Unexpected FileReader result'));
        }
      };
      reader.onerror = (e) => reject(e);
      reader.readAsDataURL(blob);
    });
  };

  const uploadPdfBytes = async (pdfBytes, fileName) => {
    const { table, sys_id, user_sys_id } = getUploadContext();
    if (!table || !sys_id) {
      Modal.warning({ title: '', content: 'Missing table/sys_id for upload. Open this editor from Smart Folders.' });
      throw new Error('Missing upload context');
    }
      // if (!user_sys_id) {
      //   Modal.warning({ title: '', content: 'Missing user_sys_id for upload. Please open this editor from Smart Folders.' });
      //   throw new Error('Missing user_sys_id');
      // }

    // Upload through the same-origin /api proxy path.
    const base64Content = await arrayBufferToBase64(pdfBytes);

    const requestHeaders = getServiceNowHeaders({
      includeBasicAuth: true,
      contentType: 'application/json',
    });
    const uploadUrl = buildServiceNowUrl(API_ATTACHMENT_UPLOAD);

    logRequestDetails('SmartFolders upload request', {
      method: 'POST',
      url: uploadUrl,
      body: {
        table_name: table,
        user_sys_id: user_sys_id,
        record_sys_id: sys_id,
        file_name: fileName,
        content_type: 'application/pdf',
        file_content_base64: `[base64:${base64Content?.length || 0} chars]`,
      },
    });

    const axiosConfig = { headers: requestHeaders };

    const uploadResponse = await axios.post(
      uploadUrl,
      {
        table_name: table,
        user_sys_id: user_sys_id,
        record_sys_id: sys_id,
        file_name: fileName,
        content_type: 'application/pdf',
        file_content_base64: base64Content,
      },
      axiosConfig
    );
    message.success(`Uploaded ${fileName}`);
    return uploadResponse;
  };

  const getBaseFileName = () => {
    try {
      const params = getAppParams();
      const fromParam = params.get('name');
      let base = fromParam ? decodeURIComponent(fromParam) : null;
      if (!base) {
        const fp = sessionStorage.getItem('filePath');
        base = fp ? String(fp).split('/').pop() : 'document.pdf';
      }
      return String(base).replace(/\.pdf$/i, '') || 'document';
    } catch (_) {
      return 'document';
    }
  };

  const parsePageGroups = (input) => {
    const result = [];
    if (!input) return result;
    const cleaned = String(input).replace(/\s+/g, "");
    if (cleaned.length === 0) return result;
    const parts = cleaned.split(",");
    for (const rawPart of parts) {
      const part = rawPart.trim();
      if (!part) continue;
      if (part.includes("-")) {
        const [startStr, endStr] = part.split("-");
        const start = parseInt(startStr, 10) - 1;
        const end = parseInt(endStr, 10) - 1;
        if (Number.isFinite(start) && Number.isFinite(end)) {
          const lo = Math.min(start, end);
          const hi = Math.max(start, end);
          result.push([lo, hi]);
        }
      } else {
        const n = parseInt(part, 10) - 1;
        if (Number.isFinite(n)) result.push([n]);
      }
    }
    return result;
  };

  const extractPagesAction = async () => {
    try {
      const pdfui = pdfuiRef.current;
      if (!pdfui) return;
      const initial = '1-2';
      const pageText = await new Promise((resolve) => {
        let val = initial;
        const modal = Modal.confirm({
          title: 'Extract Pages',
          content: (
            <div>
              <div style={{ marginBottom: 8 }}>Enter pages to extract (e.g., 2,4-5)</div>
              <Input defaultValue={initial} onChange={(e) => { val = e.target.value; }} />
            </div>
          ),
          okText: 'OK',
          cancelText: 'Cancel',
          onOk: () => resolve(val),
          onCancel: () => resolve(null),
        });
      });
      if (pageText == null) return;
      const ranges = parsePageGroups(pageText);
      if (!ranges.length) { Modal.warning({ title: '', content: 'No valid pages specified.' }); return; }
      const pdfDoc = await pdfui.getCurrentPDFDoc();
      const buffers = await pdfDoc.extractPages(ranges);
      const merged = new Blob(buffers, { type: 'application/pdf' });
      const baseName = getBaseFileName();
      const pagesForName = [];
      for (const g of ranges) {
        if (g.length === 1) pagesForName.push(g[0] + 1);
        else {
          for (let n = g[0]; n <= g[1]; n++) pagesForName.push(n + 1);
        }
      }
      const unique = Array.from(new Set(pagesForName)).sort((a, b) => a - b);
      const outName = `${baseName}_extract_${unique.join('_')}.pdf`;
      const bytes = await merged.arrayBuffer();
      await uploadPdfBytes(bytes, outName);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[Foxit] Extract pages failed', e);
      window.alert(`Failed to extract pages: ${describeAxiosError(e)}`);
    }
  };

  const burstPdfAction = async () => {
    try {
      const pdfui = pdfuiRef.current;
      if (!pdfui) return;
      const initial = '1-1';
      const input = await new Promise((resolve) => {
        let val = initial;
        Modal.confirm({
          title: 'Burst PDF',
          content: (
            <div>
              <div style={{ marginBottom: 8 }}>Enter page ranges to split into separate files (e.g., 2-4,16-23,33-36)</div>
              <Input defaultValue={initial} onChange={(e) => { val = e.target.value; }} />
            </div>
          ),
          okText: 'OK',
          cancelText: 'Cancel',
          onOk: () => resolve(val),
          onCancel: () => resolve(null),
        });
      });
      if (input == null) return;
      const groupsResult = parsePageGroups(input);
      if (!groupsResult.length) { Modal.warning({ title: '', content: 'No valid page ranges specified.' }); return; }
      const confirmed = await new Promise((resolve) => {
        Modal.confirm({ title: '', content: `This will upload ${groupsResult.length} file(s). Continue?`, onOk: () => resolve(true), onCancel: () => resolve(false) });
      });
      if (!confirmed) return;
      const pdfDoc = await pdfui.getCurrentPDFDoc();
      const baseName = getBaseFileName();
      for (let i = 0; i < groupsResult.length; i++) {
        const g = groupsResult[i];
        const buffers = await pdfDoc.extractPages([g]);
        const blob = new Blob(buffers, { type: 'application/pdf' });
        const label = g.length === 1 ? String(g[0] + 1) : `${g[0] + 1}-${g[1] + 1}`;
        const bytes = await blob.arrayBuffer();
        await uploadPdfBytes(bytes, `${baseName}_burst_${label}.pdf`);
        // Small delay to avoid browser throttling on multiple downloads
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 80));
      }
      // Success messages are shown per file in uploadPdfBytes
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[Foxit] Burst PDF failed', e);
      Modal.error({ title: '', content: `Failed to burst PDF: ${describeAxiosError(e)}` });
    }
  };

  const savePdfAction = async () => {
    try {
      const pdfui = pdfuiRef.current;
      if (!pdfui) return;
      const pdfDoc = await pdfui.getCurrentPDFDoc();
      const file = await pdfDoc.getFile();
      const bytes = await new Blob([file], { type: 'application/pdf' }).arrayBuffer();
      const base = getBaseFileName();
      await uploadPdfBytes(bytes, `${base}.pdf`);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[Foxit] Save failed', e);
      Modal.error({ title: '', content: `Failed to save PDF: ${describeAxiosError(e)}` });
    }
  };

  const saveAsPdfAction = async () => {
    try {
      const pdfui = pdfuiRef.current;
      if (!pdfui) return;
      const base = getBaseFileName();
      let value = `${base}.pdf`;
      const name = await new Promise((resolve) => {
        Modal.confirm({
          title: 'Save As',
          content: (
            <div>
              <div style={{ marginBottom: 8 }}>Enter a new file name</div>
              <Input defaultValue={value} onChange={(e) => { value = e.target.value; }} />
            </div>
          ),
          okText: 'Save',
          cancelText: 'Cancel',
          onOk: () => resolve(value),
          onCancel: () => resolve(null),
        });
      });
      if (name == null) return;
      let fileName = String(name || '').trim();
      if (!fileName) return;
      if (!/\.pdf$/i.test(fileName)) fileName = `${fileName}.pdf`;
      const pdfDoc = await pdfui.getCurrentPDFDoc();
      const file = await pdfDoc.getFile();
      const bytes = await new Blob([file], { type: 'application/pdf' }).arrayBuffer();
      await uploadPdfBytes(bytes, fileName);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[Foxit] Save As failed', e);
      Modal.error({ title: '', content: `Failed to Save As: ${describeAxiosError(e)}` });
    }
  };

  return (
    <>
      {loading && <Loader message={"Loading PDF document..."} />}
      <div style={{ height: '100vh', width: '100%', position: 'relative', overflow: 'hidden' }}>
        <div id="pdf-container" ref={containerRef} style={{ position: 'absolute', inset: 0 }} />
        {authError && (
          <DebugErrorScreen
            title="PDF editor error"
            error={authError?.error || authError?.message || authError}
            details={authError}
            onDismiss={() => {
              try { clearAuthError?.(); } catch (_) {}
            }}
          />
        )}
        {viewerInitError && (
          <DebugErrorScreen
            title="PDF viewer initialization error"
            error={viewerInitError?.message || viewerInitError}
            details={viewerInitError}
            onDismiss={() => setViewerInitError(null)}
          />
        )}
      </div>
    </>
  );
}
